/**
 * Client-only UI state.
 *
 * The second and last thing in Redux. Everything here is a user preference or a
 * piece of cross-component UI state that no server endpoint owns — which is
 * exactly the category React Query cannot help with and prop-drilling handles
 * badly.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export type ThemePreference = "light" | "dark" | "system";

export interface UiState {
  /**
   * Desktop page column (the list of screens beside the module rail) shown.
   *
   * Hidden state is the user's explicit choice and persists; clicking any rail
   * tile brings the column back. The phone sheets are NOT here — they are
   * transient and local to the phone shell.
   */
  pagePanelOpen: boolean;
  theme: ThemePreference;

  /**
   * The company every list page is filtered to, or null for "all companies".
   *
   * A single global selection rather than a filter per page: the mental model is
   * "I am looking at company X", and a dropdown on each screen would need the
   * same state five times and desynchronise between them.
   *
   * Persisted, but NOT trusted on load — a stored id can name a company whose
   * access was revoked or which was deleted, so `reconcileCompany` validates it
   * against /me before anything queries with it.
   */
  companyId: string | null;
}

// New keys only get ADDED here. Renaming an existing key silently resets that
// preference for every user.
const THEME_KEY = "yg-hr.theme";
const PAGE_PANEL_KEY = "yg-hr.pagePanelOpen";
const COMPANY_KEY = "yg-hr.company";

/**
 * Keys the old sidebar wrote and nothing reads any more. Removed at start-up so
 * they do not sit in every browser forever; harmless if absent.
 */
const RETIRED_KEYS = ["yg-hr.sidebar-collapsed", "yg-hr.nav-sections"];

/**
 * Reads a persisted preference.
 *
 * Wrapped in try/catch because localStorage throws — not returns null — when
 * access is denied by a privacy setting or when storage is full. An unhandled
 * throw here would happen during module init and take the whole app down before
 * it renders.
 */
function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value !== null && (allowed as readonly string[]).includes(value)
      ? (value as T)
      : fallback;
  } catch {
    return fallback;
  }
}

function readStoredBoolean(key: string, fallback: boolean): boolean {
  try {
    const value = localStorage.getItem(key);
    return value === null ? fallback : value === "true";
  } catch {
    return fallback;
  }
}

function readStoredCompany(): string | null {
  try {
    const value = localStorage.getItem(COMPANY_KEY);
    return value === null || value === "" ? null : value;
  } catch {
    return null;
  }
}

function persistCompany(companyId: string | null): void {
  try {
    if (companyId === null) localStorage.removeItem(COMPANY_KEY);
    else localStorage.setItem(COMPANY_KEY, companyId);
  } catch {
    // A lost preference is not worth failing the interaction over.
  }
}

function removeRetiredKeys(): void {
  try {
    for (const key of RETIRED_KEYS) localStorage.removeItem(key);
  } catch {
    // Nothing depends on this succeeding.
  }
}

removeRetiredKeys();

const initialState: UiState = {
  pagePanelOpen: readStoredBoolean(PAGE_PANEL_KEY, true),
  theme: readStored(THEME_KEY, ["light", "dark", "system"] as const, "system"),
  companyId: readStoredCompany(),
};

/** Applies the resolved theme to <html>, which is what the `dark` variant reads. */
export function applyTheme(preference: ThemePreference): void {
  const prefersDark =
    preference === "dark" ||
    (preference === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  document.documentElement.classList.toggle("dark", prefersDark);
}

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setPagePanelOpen(state, action: PayloadAction<boolean>) {
      if (state.pagePanelOpen === action.payload) return;
      state.pagePanelOpen = action.payload;
      try {
        localStorage.setItem(PAGE_PANEL_KEY, String(state.pagePanelOpen));
      } catch {
        // A lost preference is not worth failing the interaction over.
      }
    },

    /** The user's explicit choice from the header switcher. */
    setCompany(state, action: PayloadAction<string | null>) {
      state.companyId = action.payload;
      persistCompany(state.companyId);
    },

    /**
     * Validates the persisted selection against what the session actually allows.
     *
     * Three cases, and the last is the one that matters: a stored company the
     * user can no longer see must not survive, or every list page would sit on a
     * 403 forever with no obvious way for the user to recover.
     */
    reconcileCompany(
      state,
      action: PayloadAction<{ available: readonly string[]; allowAll: boolean }>,
    ) {
      const { available, allowAll } = action.payload;

      if (state.companyId !== null && available.includes(state.companyId)) return;

      // No stored choice: "all" for someone who has it, otherwise their only
      // company — a single-company operator should never see a chooser at all.
      state.companyId =
        allowAll || available.length !== 1 ? null : (available[0] ?? null);
      persistCompany(state.companyId);
    },

    setTheme(state, action: PayloadAction<ThemePreference>) {
      state.theme = action.payload;
      applyTheme(action.payload);
      try {
        localStorage.setItem(THEME_KEY, action.payload);
      } catch {
        // Same: the theme still applies for this session.
      }
    },
  },
});

export const { setPagePanelOpen, setTheme, setCompany, reconcileCompany } = uiSlice.actions;
export const uiReducer = uiSlice.reducer;
